import { engagementTypeShort } from '../utils/engagementLabel';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Briefcase, Pencil, Archive, ArchiveRestore, SearchX, Building2 } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from '../store/toastStore';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Dialog from '../components/ui/Dialog';
import Field, { FormErrorSummary, rules, useFormValidation, type FormSchema } from '../components/ui/Field';
import { FormFooter } from '../components/projects/formBits';
import { apiErrorMessage } from '../components/sales/shared';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import Badge from '../components/ui/Badge';
import FilterBar, { type FilterDef } from '../components/ui/FilterBar';
import SavedViews, { type SystemView } from '../components/ui/SavedViews';
import DataTable, { type CardSpec, type Column, type RowAction } from '../components/DataTable';
import { statusTone } from '../components/ui/tone';
import { useArrivalView } from '../components/lists/useArrivalView';
import { canonicalQuery, type ListSchema } from '../components/table/listParams';
import { useDebouncedParam, useListParams } from '../hooks/useListParams';
import EngagementArchiveDialog from '../components/engagement/EngagementArchiveDialog';
import TypedEngagementForm, { isTypedEngagementType, type TypedEngagementType } from '../components/engagement/TypedEngagementForm';
import ProjectFormModal from '../components/projects/ProjectFormModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BanksOverList from '../components/engagement/BanksOverList';
import { newBankRedirect } from '../components/engagement/hourBankView';

/** What a confirm calls the engagement: its name, else its customer. */
const engagementName = (e: Engagement) => e.child_name || e.organization_name || '';

/** Quiet placeholder for an empty cell. */
function Dash() {
  return <span className="text-surface-500" aria-hidden="true">—</span>;
}
import type { Engagement, EngagementType, EngagementStatus } from '../types';
import { archiveEntryLabelKey, offerUndoAfterArchive } from '../utils/engagementArchiveModel';
import { isWarrantyRefusal, offerUndoStatus, statusIsReversible } from '../utils/undoableActions';
import { UNDO_DEFERRED } from '../utils/undoableActionsCore';
import { OrgName } from '../components/OrgLink';
import WarrantyField from '../components/projects/WarrantyField';
import { needsWarrantyOnStatus, rowAfterStatus, warrantyMissing, warrantyPatch, type WarrantyPatch, type WarrantyValue } from '../components/projects/projectModel';
import EntityPicker from '../components/ui/EntityPicker';

/** `label` is an i18n key — render with t(o.label). */
const TYPE_OPTIONS: { value: EngagementType; label: string }[] = [
  { value: 'project', label: 'engagementDetail.types.project' },
  { value: 'development', label: 'engagementDetail.types.development' },
  { value: 'service_contract', label: 'engagementDetail.types.service_contract' },
  { value: 'hour_bank', label: 'engagementDetail.types.hour_bank' },
  { value: 'service_without_contract', label: 'engagementDetail.types.service_without_contract' },
];

/** `label` is an i18n key — render with t(o.label). */
const STATUS_OPTIONS: { value: EngagementStatus; label: string }[] = [
  { value: 'planning', label: 'engagements.statuses.planning' },
  { value: 'active', label: 'engagements.statuses.active' },
  { value: 'on_hold', label: 'engagements.statuses.on_hold' },
  { value: 'completed', label: 'engagements.statuses.completed' },
  { value: 'cancelled', label: 'engagements.statuses.cancelled' },
];

/**
 * P-02c/F-02 — a project and a development share the five statuses;
 * 'planning' is not offered for a service contract / hour bank / לל"ח.
 */
const hasPlanning = (type: EngagementType) => type === 'project' || type === 'development';
const statusOptionsFor = (type: EngagementType) =>
  hasPlanning(type) ? STATUS_OPTIONS : STATUS_OPTIONS.filter((o) => o.value !== 'planning');
const defaultStatusFor = (type: EngagementType): EngagementStatus => (hasPlanning(type) ? 'planning' : 'active');

/**
 * /engagements on DataTable v2 (fix round 3, principle 0): view switcher ·
 * search (in the browser, over the loaded list) · "סינון (n)" (type, status,
 * archive; to the server as before). Filters live in the URL.
 */
const ENG_PARAMS = {
  q: { kind: 'string' },
  type: { kind: 'enum', values: TYPE_OPTIONS.map((o) => o.value) },
  status: { kind: 'enum', values: STATUS_OPTIONS.map((o) => o.value) },
  archived: { kind: 'bool' },
} satisfies ListSchema;
const ENG_VIEW_KEYS = ['type', 'status', 'archived'] as const;
const ENG_LIST_ID = 'engagements';
const engViewQuery = (v: Partial<Record<(typeof ENG_VIEW_KEYS)[number], string | boolean>>) => canonicalQuery(ENG_PARAMS, v as never, ENG_VIEW_KEYS);

/**
 * Canonical "create an Engagement for a Customer" modal.
 *
 * Exported so the Customer Hub (Repair 5) can open the SAME flow instead of
 * growing a parallel modal. `lockedOrganizationId` is the Hub's addition: it
 * pins the Customer and hides the org picker, so a non-admin (whose branch
 * would otherwise send no `organization_id` at all) can still create an
 * Engagement on the Customer whose page they are on.
 */
export function CreateEngagementModal({ onClose, isLoading, onSubmit, lockedOrganizationId, initialType }: {
  onClose: () => void;
  isLoading: boolean;
  onSubmit: (data: { type: EngagementType; name: string; status: EngagementStatus; organization_id?: string }) => void;
  lockedOrganizationId?: string;
  /** P-01/C2 — the type chosen before opening (still editable in the form). */
  initialType?: EngagementType;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  // OTE staff name the Customer explicitly — the server derives no default
  // from the creator's own organization and checks the caller's link to it.
  const isStaff = user?.role === 'admin' || user?.role === 'agent';

  const [type, setType] = useState<EngagementType>(initialType ?? 'project');
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');

  // P-02c/F-01 — a new engagement is for an active customer (from the
  // customer's own page the customer is locked and this list is not loaded).
  const { data: orgsRes } = useQuery({
    queryKey: ['organizations-for-engagement', 'active'],
    queryFn: () => api.getOrganizations({ limit: 200, status: 'active' }),
    staleTime: 60_000,
    enabled: isStaff && !lockedOrganizationId,
  });
  const orgs: { id: string; name: string }[] = orgsRes?.data ?? [];

  const needsOrg = isStaff && !lockedOrganizationId;
  const schema: FormSchema = needsOrg
    ? { 'eng-org': [rules.required('engagements.form.orgRequired')], 'eng-name': [rules.required('engagements.form.nameRequired')] }
    : { 'eng-name': [rules.required('engagements.form.nameRequired')] };
  const v = useFormValidation(schema, { 'eng-org': orgId, 'eng-name': name });
  const labels = { 'eng-org': t('engagements.organization'), 'eng-name': t('engagements.name') };

  const handleSubmit = v.submit(() => {
    const targetOrg = lockedOrganizationId || orgId;
    onSubmit({
      type,
      name: name.trim(),
      // QA-R1 #8 — no status field: a service contract / hour bank / לל"ח
      // starts 'active'; a project / development continues in its own form.
      status: defaultStatusFor(type),
      ...(targetOrg ? { organization_id: targetOrg } : {}),
    });
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('engagements.new')}
      description={lockedOrganizationId ? t('organization.engagementForCustomer') : undefined}
      size="lg"
      dismissible={!isLoading}
      footer={<FormFooter formId="eng-create-form" busy={isLoading} submitLabel={t('engagements.createAction')} onCancel={onClose} />}
    >
      {/* D-316 — compact: customer and type side by side, the name full width. */}
      <form id="eng-create-form" onSubmit={handleSubmit} noValidate className="space-y-3">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={labels} order={Object.keys(schema)} />}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {needsOrg && (
          <Field id="eng-org" label={labels['eng-org']} required error={v.errors['eng-org']}>
            <EntityPicker
              icon={Building2}
              placeholder={t('engagements.pickOrg')}
              options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              value={orgId}
              onChange={setOrgId}
              onBlur={() => v.blur('eng-org')}
            />
          </Field>
        )}
        <Field id="eng-type" label={t('engagements.type')} required>
          <select
            value={type}
            onChange={(e) => {
              const next = e.target.value as EngagementType;
              setType(next);
            }}
            className="input"
          >
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
          </select>
        </Field>
        </div>
        <Field id="eng-name" label={labels['eng-name']} required error={v.errors['eng-name']}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => v.blur('eng-name')}
            className="input"
            autoComplete="off"
          />
        </Field>
      </form>
    </Dialog>
  );
}

/** D-347 §1: `?banks_over=1` shows the hour banks over their threshold instead of the engagements list. */
export default function EngagementsPage() {
  const [params] = useSearchParams();
  return params.get('banks_over') === '1' ? <BanksOverList /> : <EngagementsList />;
}

function EngagementsList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  // Engagement writes are OTE-staff only (server-enforced); a customer
  // manager login sees its engagements read-only.
  const canManage = isAdmin || user?.role === 'agent';

  const list = useListParams(ENG_PARAMS);
  const f = list.values;
  useArrivalView(list, ENG_VIEW_KEYS, ENG_LIST_ID, user?.id);
  const [searchText, setSearchText] = useDebouncedParam(f.q, (q) => list.set({ q }, { replace: true }));
  const typeFilter = f.type as '' | EngagementType;
  const statusFilter = f.status as '' | EngagementStatus;
  const includeDeleted = f.archived;

  const [showCreate, setShowCreate] = useState(false);
  // P-03/B — a service contract / hour bank / לל"ח continues in its own form
  // (the general form's customer + name carried over).
  const [typedCreate, setTypedCreate] = useState<{ type: TypedEngagementType; organizationId: string; name: string } | null>(null);
  // P-04/B — a project / development opens the project form.
  const [projectCreate, setProjectCreate] = useState<{ kind: 'project' | 'development'; organizationId: string; name: string } | null>(null);
  const navigate = useNavigate();
  const [statusTarget, setStatusTarget] = useState<Engagement | null>(null);
  const [newStatus, setNewStatus] = useState<EngagementStatus>('active');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  // D-303: ending a project / development asks for "אחריות ללקוח" (a date, or "אין אחריות").
  const [warrantyValue, setWarrantyValue] = useState<WarrantyValue>({ until: '', none: false });
  const [warrantyError, setWarrantyError] = useState('');
  const [archiveTarget, setArchiveTarget] = useState<Engagement | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<Engagement | null>(null);

  const { data, isLoading, isError, error: loadError, refetch } = useQuery({
    queryKey: ['engagements', typeFilter, statusFilter, includeDeleted],
    queryFn: () => api.getEngagements({
      limit: 200,
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      include_deleted: includeDeleted || undefined,
    }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['engagements'] });

  const createMutation = useMutation({
    mutationFn: (d: { type: EngagementType; name: string; status: EngagementStatus; organization_id?: string }) =>
      api.createEngagement(d),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      toast('success', t('engagements.toast.created'));
    },
    onError: (err: unknown) => {
      toast('error', apiErrorMessage(err, t('engagements.errors.create')));
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, reason, warranty }: { id: string; status: EngagementStatus; reason?: string; warranty?: WarrantyPatch }) =>
      api.updateEngagementStatus(id, status, reason, warranty),
    onMutate: () => ({ prev: statusTarget?.child_status ?? undefined }),
    onSuccess: (_r, vars, ctx) => {
      invalidate();
      const target = statusTarget;
      setStatusTarget(null);
      // P-27 (D-166 s.11): "בטל" sends the previous status through the same route. Not offered
      // into / out of "בוטל" (it stores a cancellation reason that a status revert would keep).
      if (statusIsReversible(ctx?.prev, vars.status, ['cancelled'])) {
        offerUndoStatus({
          message: t('undoToast.statusChanged', { status: t(`engagements.statuses.${vars.status}`) }),
          // D-303: going back INTO "הסתיים" of an old project / development with no warranty opens the warranty window (not an error).
          revert: async () => {
            try {
              return await api.updateEngagementStatus(vars.id, ctx!.prev as EngagementStatus);
            } catch (err) {
              if (target && needsWarrantyOnStatus(target.type, vars.status, ctx?.prev as string) && isWarrantyRefusal(err)) {
                // The row is opened as it is NOW (after the change): re-entering "הסתיים" must show the warranty field.
                setStatusTarget(rowAfterStatus(target, vars.status)); setNewStatus('completed'); setCancelReason(''); setWarrantyValue({ until: '', none: false }); setWarrantyError('');
                return UNDO_DEFERRED;
              }
              throw err;
            }
          },
          onReverted: invalidate,
        });
      } else toast('success', t('engagements.toast.statusUpdated'));
    },
    onError: (err: unknown) => {
      toast('error', apiErrorMessage(err, t('engagements.errors.status')));
    },
  });

  // D-265 sec.2-3 + QX-1273: the "ארכיון / מחיקה" window (what goes with it, timers, the regular /
  // permanent delete). The message appears only after the server answered. "בטל" after an archive
  // only for who may restore - the OTE manager (D-166 sec.11, Q-215); timers do not run again.
  const onArchiveDone = (kind: 'archived' | 'purged' | 'deleted', info: { canRestore: boolean }) => {
    const archivedId = archiveTarget?.id;
    invalidate();
    void queryClient.invalidateQueries({ queryKey: ['engagement'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    setArchiveTarget(null);
    if (archivedId && offerUndoAfterArchive(kind, info.canRestore)) {
      offerUndoStatus({ message: t('engagements.toast.archived'), revert: () => api.restoreEngagement(archivedId), onReverted: invalidate });
      return;
    }
    toast('success', kind === 'purged' ? t('engagements.archiveDialog.purged')
      : kind === 'deleted' ? t('undoToast.engagementDeleted') : t('engagements.toast.archived'));
  };

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.restoreEngagement(id),
    onSuccess: () => {
      invalidate();
      setRestoreTarget(null);
      toast('success', t('engagements.toast.restored'));
    },
    onError: (err: unknown) => {
      toast('error', apiErrorMessage(err, t('engagements.errors.restore')));
    },
  });

  const allEngagements = useMemo(() => data?.data ?? [], [data]);
  const engagements = allEngagements;
  const hasServerFilters = !!f.type || !!f.status || f.archived;
  const hasFilters = hasServerFilters || !!f.q;
  const clearFilters = () => list.set({ q: '', type: '', status: '', archived: false });

  // Search narrows the loaded list (up to 200) in the browser; type, status and the archive go to the server.
  const needle = f.q.trim().toLowerCase();
  const shown = needle
    ? engagements.filter((e) => [e.child_name, e.organization_name].some((s) => (s ?? '').toLowerCase().includes(needle)))
    : engagements;

  const typeLabelOf = (eng: Engagement) => {
    // F1-2 — rows show the short "לל\"ח".
    const o = TYPE_OPTIONS.find((x) => x.value === eng.type);
    return o ? engagementTypeShort(eng.type, t) : eng.type;
  };
  const statusBadges = (eng: Engagement) => {
    const so = eng.child_status ? STATUS_OPTIONS.find((o) => o.value === eng.child_status) : undefined;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        {eng.child_status ? <Badge tone={statusTone(eng.child_status)} dot>{so ? t(so.label) : eng.child_status}</Badge> : <Dash />}
        {eng.deleted_at && <Badge>{t('uxLists.archived')}</Badge>}
      </span>
    );
  };
  const rowName = (eng: Engagement) => eng.child_name || eng.id;
  const needsWarranty = !!statusTarget && needsWarrantyOnStatus(statusTarget.type, statusTarget.child_status, newStatus);
  const needsCancelReason = !!statusTarget && hasPlanning(statusTarget.type)
    && newStatus === 'cancelled' && statusTarget.child_status !== 'cancelled';

  const filters: FilterDef[] = [
    {
      key: 'type',
      label: t('engagements.type'),
      value: f.type,
      options: TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
      allLabel: t('engagements.allTypes'),
      onChange: (type) => list.set({ type }),
    },
    {
      key: 'status',
      label: t('engagements.status'),
      value: f.status,
      options: STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
      allLabel: t('engagements.allStatuses'),
      onChange: (status) => list.set({ status }),
    },
    {
      key: 'archived',
      label: t('listsV2.archive.label'),
      value: f.archived ? '1' : '',
      options: [{ value: '1', label: t('listsV2.archive.include') }],
      allLabel: t('listsV2.archive.exclude'),
      onChange: (v) => list.set({ archived: v === '1' }),
    },
  ];

  const columns: Column<Engagement>[] = [
    {
      key: 'name',
      header: t('engagements.name'),
      link: true,
      render: (eng) => <bdi className={eng.deleted_at ? 'text-text-2' : undefined}>{rowName(eng)}</bdi>,
    },
    {
      key: 'organization',
      header: t('engagements.organization'),
      priority: 2,
      width: 'w-48',
      render: (eng) => (eng.organization_name ? <span className="block max-w-48 truncate text-text-2"><OrgName orgId={eng.organization_id} name={eng.organization_name} /></span> : <Dash />),
    },
    {
      key: 'type',
      header: t('engagements.type'),
      priority: 2,
      width: 'w-40',
      render: (eng) => <span className="whitespace-nowrap text-text-2">{typeLabelOf(eng)}</span>,
    },
    { key: 'status', header: t('engagements.status'), width: 'w-36', render: statusBadges },
  ];

  const card = (eng: Engagement): CardSpec => ({
    title: <bdi>{rowName(eng)}</bdi>,
    badges: statusBadges(eng),
    metaItems: [eng.organization_name ? <OrgName orgId={eng.organization_id} name={eng.organization_name} /> : null, typeLabelOf(eng)],
  });

  // D5: the status change shows on hover / focus; archive, restore and delete live in ⋯ (on touch: all in ⋯).
  const rowActions = (eng: Engagement): RowAction[] => {
    if (!canManage) return [];
    const isArchived = !!eng.deleted_at;
    const actions: RowAction[] = eng.archived_at ? [] : [
      {
        key: 'status',
        label: t('engagements.changeStatus'),
        icon: <Pencil size={16} strokeWidth={1.75} />,
        inline: true,
        onSelect: () => {
          setStatusTarget(eng);
          setNewStatus((eng.child_status as EngagementStatus) || 'active');
          setCancelReason('');
          setCancelReasonError('');
          setWarrantyValue({ until: '', none: false });
          setWarrantyError('');
        },
      },
    ];
    // D-265 (Q-215): an engagement archived with its contents is restored by an OTE manager only
    // (and is read only: no status change).
    if (isArchived && (!eng.archived_at || isAdmin)) actions.push({ key: 'restore', label: t('engagements.restoreFromArchive'), icon: <ArchiveRestore size={16} strokeWidth={1.75} />, onSelect: () => setRestoreTarget(eng) });
    // QX-1273: ONE entry "ארכיון / מחיקה" opens the window (archive; the regular delete when nothing is
    // under it; the permanent delete for the OTE manager). In the archive: the OTE manager's deletes only.
    if (!isArchived || isAdmin) {
      actions.push({ key: 'archive_delete', label: t(archiveEntryLabelKey(isArchived)), icon: <Archive size={16} strokeWidth={1.75} />, separatorBefore: isArchived, onSelect: () => setArchiveTarget(eng) });
    }
    return actions;
  };

  const addButton = canManage ? (
    <button type="button" onClick={() => setShowCreate(true)} className="btn btn-primary">
      <Plus className="w-4 h-4" aria-hidden="true" /> {t('engagements.new')}
    </button>
  ) : undefined;

  const systemViews: SystemView[] = [
    { id: 'all', name: t('listsV2.views.allEngagements'), query: engViewQuery({}) },
    { id: 'active', name: t('listsV2.views.activeEngagements'), query: engViewQuery({ status: 'active' }) },
  ];

  return (
    <div>
      <PageHeader title={t('engagements.title')} actions={addButton} />

      <FilterBar
        className="mb-4"
        leading={(
          <SavedViews
            listId={ENG_LIST_ID}
            systemViews={systemViews}
            currentQuery={list.query(ENG_VIEW_KEYS)}
            onApply={(q) => list.applyView(q, ENG_VIEW_KEYS)}
          />
        )}
        search={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('listsV2.search.engagements')}
        filters={filters}
        onClearAll={clearFilters}
        onClearFilters={() => list.set({ type: '', status: '', archived: false })}
        summary={!isLoading && !isError ? t('uxLists.engagementsCount', { count: shown.length }) : null}
      />

      <DataTable
        columns={columns}
        data={shown}
        keyField="id"
        caption={t('engagements.title')}
        mobile="cards"
        getRowHref={(eng) => `/engagements/${eng.id}`}
        rowLabel={rowName}
        card={card}
        rowActions={canManage ? rowActions : undefined}
        isLoading={isLoading}
        skeletonRows={8}
        error={isError ? loadError ?? true : undefined}
        errorMessage={isError ? apiErrorMessage(loadError, t('common.loadFailed')) : undefined}
        onRetry={() => { void refetch(); }}
        emptyState={(
          <EmptyState
            icon={hasFilters ? SearchX : Briefcase}
            title={hasFilters ? t('uxLists.noMatchesTitle') : t('engagements.noEngagements')}
            description={hasFilters ? t('uxLists.noMatchesDesc') : t('uxLists.engagementsEmptyDesc')}
            action={hasFilters ? (
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>{t('common.clearFilter')}</button>
            ) : addButton}
          />
        )}
      />

      {showCreate && (
        <CreateEngagementModal
          onClose={() => setShowCreate(false)}
          isLoading={createMutation.isPending}
          onSubmit={(data) => {
            if (isTypedEngagementType(data.type) && data.organization_id) {
              const typedNext = { type: data.type, organizationId: data.organization_id, name: data.name };
              setShowCreate(false);
              if (data.type !== 'hour_bank') { setTypedCreate(typedNext); return; }
              // D-272: a customer with an active bank -> that bank's "new purchase" (same rule as the server's 409 guard).
              void newBankRedirect(() => api.getActiveHourBank(typedNext.organizationId)).then((path) => {
                if (path) navigate(path); else setTypedCreate(typedNext);
              });
              return;
            }
            if ((data.type === 'project' || data.type === 'development') && data.organization_id) {
              setShowCreate(false);
              setProjectCreate({ kind: data.type, organizationId: data.organization_id, name: data.name });
              return;
            }
            createMutation.mutate(data);
          }}
        />
      )}
      {typedCreate && (
        <TypedEngagementForm
          mode="create"
          type={typedCreate.type}
          organizationId={typedCreate.organizationId}
          initialName={typedCreate.name}
          onClose={() => setTypedCreate(null)}
          onSaved={() => {
            invalidate();
            setTypedCreate(null);
            toast('success', t('engagements.toast.created'));
          }}
        />
      )}
      {projectCreate && (
        <ProjectFormModal
          mode="create"
          kind={projectCreate.kind}
          organizationId={projectCreate.organizationId}
          initialName={projectCreate.name}
          onClose={() => setProjectCreate(null)}
          onSaved={(projectId) => {
            invalidate();
            setProjectCreate(null);
            navigate(`/projects/${projectId}`);
          }}
        />
      )}

      <Dialog
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        title={t('engagements.changeStatus')}
        description={statusTarget ? engagementName(statusTarget) : undefined}
        size="sm"
        dismissible={!statusMutation.isPending}
        footer={(
          <FormFooter
            formId="eng-status-form"
            busy={statusMutation.isPending}
            submitLabel={t('engagements.saveStatus')}
            onCancel={() => setStatusTarget(null)}
          />
        )}
      >
        <form
          id="eng-status-form"
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!statusTarget) return;
            // P-02c/F-03 — cancelling a project / development needs a reason.
            if (needsCancelReason && !cancelReason.trim()) {
              setCancelReasonError(t('project.cancellationReasonRequired'));
              return;
            }
            if (needsWarranty && warrantyMissing(warrantyValue)) {
              setWarrantyError(t('warranty.required'));
              return;
            }
            statusMutation.mutate({
              id: statusTarget.id,
              status: newStatus,
              ...(needsCancelReason ? { reason: cancelReason.trim() } : {}),
              ...(needsWarranty ? { warranty: warrantyPatch(warrantyValue) } : {}),
            });
          }}
        >
          <Field id="eng-new-status" label={t('engagements.status')}>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as EngagementStatus)}
              className="input w-full"
            >
              {(statusTarget ? statusOptionsFor(statusTarget.type) : STATUS_OPTIONS).map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
            </select>
          </Field>
          {needsWarranty && (
            <WarrantyField
              id="eng-status-warranty"
              value={warrantyValue}
              onChange={(v) => { setWarrantyValue(v); setWarrantyError(''); }}
              error={warrantyError || undefined}
              required
              hint={t('warranty.intro')}
            />
          )}
          {needsCancelReason && (
            <Field id="eng-cancel-reason" label={t('project.cancellationReason')} required error={cancelReasonError || undefined}>
              <textarea
                value={cancelReason}
                onChange={(e) => { setCancelReason(e.target.value); setCancelReasonError(''); }}
                className="input w-full"
                rows={3}
              />
            </Field>
          )}
        </form>
      </Dialog>

      <EngagementArchiveDialog
        open={!!archiveTarget}
        engagementId={archiveTarget?.id ?? ''}
        name={archiveTarget ? engagementName(archiveTarget) : ''}
        onClose={() => setArchiveTarget(null)}
        onDone={onArchiveDone}
        onError={(m) => toast('error', m)}
      />

      <ConfirmDialog
        open={!!restoreTarget}
        title={t('engagements.restoreTitleNamed', { name: restoreTarget ? engagementName(restoreTarget) : '' })}
        description={t('engagements.restoreConsequence')}
        confirmLabel={t('engagements.restore')}
        loading={restoreMutation.isPending}
        onConfirm={() => { if (restoreTarget) restoreMutation.mutate(restoreTarget.id); }}
        onCancel={() => setRestoreTarget(null)}
      />
    </div>
  );
}
